import { Formik } from 'formik';
import React, { useContext, useEffect, useState } from 'react';
import { Route, useHistory } from 'react-router-dom';

import { TrackingContext } from '../../../TrackingContext';
import { messagingHubRoute } from '../../../helpers/routes';
import { SmsPreviewContainer } from '../../MessagingHub/SmsPreview/SmsPreviewContainer';
import FullScreenModal from '../../Modals/FullScreenModal';
import FullScreenHeader from '../../Modals/FullScreenModal/Header';
import AddGuestRail, { Household } from '../AddGuestRail';
import { EmailSequenceProps } from '../EmailSequence';
import { cleanSetEditHousehold, sanitizeCollectionForGDS } from '../helper';
import HouseholdRail from '../HouseholdRail';
import ReCaptcha from '../ReCaptcha';
import styles from '../share.scss';
import { smsSharingSchema } from '../sharingValidation';
import SmsEditorRail from './SmsEditorRail';
import SendConfirmation from '../SendConfirmation';
import { findLeader } from '../../../helpers/households';

export interface ShareValues {
  greeting?: string;
  message: string;
  optout?: string;
}

interface Props extends Omit<EmailSequenceProps, 'PreviewComponent' | 'EditorComponent' | 'validationSchema'> {
  wwsLoading: boolean;
  smsLink?: string;
}

const SmsSequence: React.FC<Props> = ({
  onSend,
  loading,
  showError,
  errorMessage,
  showSuccess,
  setGuestIds,
  initialValues,
  hideRecaptcha,
  setHideRecaptcha,
  recaptchaRef,
  editPath,
  editRoute,
  households,
  wwsLoading,
  householdLoading,
  HouseholdRailComponent = HouseholdRail,
  smsLink,
  displayName,
  setFilter,
  filterPreset,
}) => {
  const [guestRailOpen, setGuestRailOpen] = useState(false);
  const [source, setSource] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editHousehold, setEditHousehold] = useState<Household>();
  const [householdsList, setHouseholdsList] = useState<Household[]>();
  const history = useHistory();
  const [selectedHouseholds, setSelectedHouseholds] = useState<Set<string>>(new Set());

  const { setPagesLoaded } = useContext(TrackingContext);
  useEffect(() => {
    setPagesLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (showError || showSuccess) {
      setIsSubmitting(false);
    }
  }, [showError, showSuccess]);

  // Add timeout to prevent stuck loading states
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (isSubmitting && !showSuccess && !showError) {
      timeout = setTimeout(() => {
        setIsSubmitting(false);
      }, 30000); // 30-second timeout
    }
    return () => clearTimeout(timeout);
  }, [isSubmitting, showSuccess, showError]);

  const queryParams = new URLSearchParams(window.location.search);
  const composeSource = queryParams.get('source');

  if (composeSource && composeSource !== source) {
    setSource(composeSource);
  }

  // Take user to where they came from
  const backHandler = () => {
    let pathname = window.location.pathname.replace('/gs/dashboard', '');
    if (pathname.indexOf('/edit') !== -1) {
      exitSequenceHandler();
    } else {
      history.push(messagingHubRoute);
    }
  };

  const exitSequenceHandler = () => {
    if (source) {
      window.location.replace(source);
    } else {
      history.push(messagingHubRoute);
    }
  };

  useEffect(() => {
    const ids = new Set<string>();

    households.contactable.forEach(h => {
      const leader = findLeader(h)!;
      ids.add(leader.id);
    });

    setSelectedHouseholds(ids);
  }, [households, setSelectedHouseholds]);

  const showRecaptcha = (guestIds: string[]) => {
    setGuestIds(guestIds);
    setHideRecaptcha(false);
  };

  return (
    <Formik enableReinitialize initialValues={initialValues} onSubmit={onSend} validationSchema={smsSharingSchema}>
      {({ submitForm }) => (
        <FullScreenModal>
          <div className={styles.shareContainer} data-testid="share-page">
            <div className={styles.sequenceHeader}>
              <FullScreenHeader backText="Back" onBack={backHandler} />
            </div>
            <HouseholdRailComponent
              sendMessage={showRecaptcha}
              showSpinner={loading}
              showSuccess={showSuccess}
              showError={showError}
              errorMessage={errorMessage}
              onHideSuccess={exitSequenceHandler}
              setGuestRailOpen={setGuestRailOpen}
              setEditHousehold={(household: Household) => cleanSetEditHousehold(household, setEditHousehold)}
              setHouseholdsList={(households: Household[]) =>
                setHouseholdsList(() => sanitizeCollectionForGDS(households))
              }
              editRoute={editRoute}
              households={households}
              householdRailType="phone"
              householdLoading={householdLoading}
              selectedHouseholds={selectedHouseholds}
              setSelectedHouseholds={setSelectedHouseholds}
              RecaptchaComponent={
                <ReCaptcha
                  recaptchaRef={recaptchaRef}
                  submitForm={submitForm}
                  hideRecaptcha={hideRecaptcha}
                  setHideRecaptcha={setHideRecaptcha}
                  setIsSubmitting={setIsSubmitting}
                />
              }
              SuccessComponent={
                <SendConfirmation showSuccess={showSuccess} showError={showError} isSubmitting={isSubmitting} />
              }
              setFilter={setFilter}
              filterPreset={filterPreset}
              messageType={displayName}
              messageMedium="sms"
            />
            <Route path={editPath}>
              <SmsEditorRail matchPath={editPath} loading={wwsLoading} smsLink={smsLink} />
            </Route>
            <AddGuestRail
              editHousehold={editHousehold}
              setEditHousehold={setEditHousehold}
              setHouseholdsList={setHouseholdsList}
              guestRailOpen={guestRailOpen}
              setGuestRailOpen={setGuestRailOpen}
              sequence={'phone'}
              selectedHouseholds={selectedHouseholds}
              setSelectedHouseholds={setSelectedHouseholds}
              householdsList={householdsList}
              messageType={displayName}
              messageMedium="sms"
            />
            <SmsPreviewContainer
              editRoute={editRoute}
              matchPath={editPath}
              smsLink={smsLink}
              loading={wwsLoading}
              displayName={displayName}
            />
          </div>
        </FullScreenModal>
      )}
    </Formik>
  );
};

export default SmsSequence;
